import React, { useState, useEffect } from 'react';
import { submitDeletionRequest } from '../services/deletionService';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';

interface FinalConfirmationProps {
  onConfirm: (requestId: string) => void;
  onCancel: () => void;
}

const FinalConfirmation: React.FC<FinalConfirmationProps> = ({ onConfirm, onCancel }) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const REQUIRED_TEXT = 'DELETE MY ACCOUNT';
  const isConfirmed = confirmationText === REQUIRED_TEXT;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsAuthenticated(!!user);
      if (!user) {
        setError({ message: 'You must be logged in to delete your account. Please refresh the page and try again.' });
      }
    });
    return () => unsubscribe();
  }, []);

  const handleConfirm = async () => {
    if (isConfirmed) {
      setIsLoading(true);
      setError(null);
      
      // Double-check authentication state
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError({ message: 'Authentication session expired. Please refresh the page and log in again.' });
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await submitDeletionRequest();
        onConfirm(response.requestId);
      } catch (error: any) {
        console.error('Failed to submit deletion request:', error);
        const errorMessage = error.message || 'Unknown error occurred';
        const errorCode = error.code || 'unknown';
        setError({ message: errorMessage, code: errorCode });
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="final-confirmation">
      <div className="warning-box">
        <h2>⚠️ Final Warning</h2>
        <p>This is your final opportunity to cancel</p>
        <ul>
          <li>Your data deletion request will be processed within 30 days</li>
          <li>Your account will be immediately deactivated</li>
          <li>All your XETA services will stop working</li>
        </ul>
      </div>

      <div className="confirmation-input">
        <p>
          To confirm deletion, type <strong>{REQUIRED_TEXT}</strong> below:
        </p>
        <input
          type="text"
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          placeholder="Type the confirmation text"
          disabled={isLoading}
        />
      </div>

      <div className="button-group">
        <button onClick={onCancel} className="cancel-button" disabled={isLoading}>
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          className="confirm-deletion-button"
          disabled={!isConfirmed || isLoading}
        >
          {isLoading ? 'Processing...' : 'Confirm Deletion'}
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ 
          marginTop: '20px', 
          padding: '15px', 
          backgroundColor: '#fee', 
          border: '1px solid #fcc',
          borderRadius: '4px',
          color: '#c00'
        }}>
          <strong>Failed to submit deletion request</strong>
          <p>Error: {error.message}</p>
          {error.code && <p>Code: {error.code}</p>}
          <p style={{ fontSize: '0.9em', marginTop: '10px' }}>
            Please check the browser console for additional details.
          </p>
        </div>
      )}
    </div>
  );
};

export default FinalConfirmation;
